import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, FileText, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateTasmiPDF } from '@/utils/pdfGenerator';
import { resolveImgUrl } from '@/utils/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KELAS_MAP = { D: "I'dad", C: 'Uula', B: 'Wustha', A: 'Ulya', M: 'Ali/Mahir' };
const KELAS_COLOR = {
  D: 'bg-amber-400/20 text-amber-700',   C: 'bg-yellow-400/20 text-yellow-700',
  B: 'bg-blue-400/20 text-blue-700',     A: 'bg-emerald-400/20 text-emerald-700',
  M: 'bg-purple-400/20 text-purple-700',
};

function formatJuz(t) {
  if (!t) return '-';
  if (t.tipe === 'multi') return `Juz ${t.juz_dari}–${t.juz_sampai} (${t.jumlah_juz} Juz)`;
  return `Juz ${t.juz ?? '-'}`;
}
const nilaiTasmi = (t) => t?.penilaian || (t?.keterangan === 'Remedial' ? 'Remedial' : (t?.keterangan ? 'A' : '—'));
const nilaiCls = (v) => v === 'Remedial' ? 'bg-red-100 text-red-600' : v === '—' ? 'bg-gray-100 text-gray-500' : 'bg-emerald-100 text-emerald-700';
function formatDurasi(t) {
  if (!t?.jam_mulai && !t?.jam_selesai) return '—';
  return `${t.jam_mulai || '?'} – ${t.jam_selesai || '?'} WIB`;
}

export default function TasmiPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [santri, setSantri] = useState(null);
  const [tasmiList, setTasmiList] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showKartu, setShowKartu] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const sid = user?.santri_id;
    if (!sid) { setLoading(false); return; }

    Promise.all([
      axios.get(`${API}/santri/${sid}`),
      axios.get(`${API}/tasmi?santri_id=${sid}`),
      axios.get(`${API}/settings`),
      axios.get(`${API}/halaqah`),
    ]).then(([s, t, st, h]) => {
      const santriData = s.data;
      const halaqahNama = h.data.find(hq => hq.id === santriData.halaqah_id)?.nama || '-';
      setSantri({ ...santriData, halaqah_nama: halaqahNama });
      setTasmiList(t.data);
      setSettings(st.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const handleUnduhPDF = async () => {
    if (!santri) return;
    setDownloading(true);
    try {
      await generateTasmiPDF(santri, tasmiList, settings);
    } catch { }
    finally { setDownloading(false); }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-950 via-emerald-800 to-emerald-700 px-5 pt-12 pb-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <button onClick={() => navigate('/santri')}
              className="p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="font-playfair text-xl font-bold text-white">Tasmi'</h1>
              <p className="text-emerald-300 text-xs mt-0.5">Riwayat ujian hafalan saya</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-3 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Profile Card */}
              {santri && (
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl overflow-hidden bg-emerald-100 flex items-center justify-center flex-shrink-0 ring-2 ring-emerald-200">
                      {santri.foto
                        ? <img src={resolveImgUrl(santri.foto)} alt={santri.name} className="w-full h-full object-cover" />
                        : <span className="text-emerald-600 font-bold text-xl">{santri.name?.charAt(0)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-playfair text-base font-bold text-gray-900 leading-tight">{santri.name}</h2>
                      <p className="text-xs text-gray-400 mt-0.5">NIS: {santri.nis || '-'}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {santri.kelas && (
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${KELAS_COLOR[santri.kelas] || 'bg-gray-100 text-gray-600'}`}>
                            Kelas {santri.kelas} · {KELAS_MAP[santri.kelas]}
                          </span>
                        )}
                        {santri.halaqah_nama && (
                          <span className="text-[11px] text-gray-400">{santri.halaqah_nama}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="tasmi-lihat-kartu-btn"
                  onClick={() => setShowKartu(true)}
                  className="flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-2xl shadow-sm transition-all">
                  <Eye size={16} /> Lihat Kartu
                </button>
                <button data-testid="tasmi-unduh-pdf-btn"
                  onClick={handleUnduhPDF} disabled={downloading}
                  className="flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-2xl shadow-sm transition-all disabled:opacity-60">
                  {downloading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <FileText size={16} />}
                  Unduh PDF
                </button>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total Tasmi\'', value: tasmiList.length },
                  { label: 'Lulus', value: tasmiList.filter(t => t.keterangan === 'Lulus').length, color: 'text-emerald-600' },
                  { label: 'Remedial', value: tasmiList.filter(t => t.keterangan === 'Remedial').length, color: 'text-red-500' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white rounded-2xl p-3 text-center shadow-sm border border-gray-100">
                    <p className={`text-xl font-bold ${color || 'text-gray-800'}`}>{value}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{label}</p>
                  </div>
                ))}
              </div>

              {/* Tabel Riwayat Tasmi' */}
              <div data-testid="tasmi-tabel" className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="text-sm font-bold text-gray-700">Riwayat Tasmi'</p>
                  <p className="text-xs text-gray-400 mt-0.5">{tasmiList.length} catatan</p>
                </div>

                {tasmiList.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <FileText size={24} className="text-emerald-300" />
                    </div>
                    <p className="text-gray-400 text-sm">Belum ada catatan Tasmi'</p>
                    <p className="text-gray-300 text-xs mt-1">Data akan muncul setelah guru menginput</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[580px] text-xs">
                      <thead>
                        <tr className="bg-emerald-900">
                          {['No','Juz','Durasi','Tanggal','Tempat Tasmi\'','Penyimak','Jml Jali','Nilai'].map(h => (
                            <th key={h} className="px-3 py-3 text-left text-white font-semibold text-[11px] whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tasmiList.map((t, i) => (
                          <motion.tr key={t.id}
                            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40'}>
                            <td className="px-3 py-2.5 text-gray-400 font-medium">{i + 1}</td>
                            <td className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">{formatJuz(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{formatDurasi(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{t.tanggal || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600">{t.tempat || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600">{t.penyimak || '—'}</td>
                            <td className="px-3 py-2.5 text-center font-medium">{t.jumlah_jali ?? '—'}</td>
                            <td className="px-3 py-2.5">
                              <span className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${nilaiCls(nilaiTasmi(t))}`}>{nilaiTasmi(t)}</span>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── KARTU TASMI' PREVIEW (FULLSCREEN) ── */}
      <AnimatePresence>
        {showKartu && santri && (
          <motion.div data-testid="tasmi-kartu-preview"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className="fixed inset-0 z-[9999] bg-gray-900 flex flex-col">

            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-950 to-emerald-800 px-5 pt-10 pb-4 flex items-center gap-3 flex-shrink-0">
              <button data-testid="kartu-tutup-btn" onClick={() => setShowKartu(false)}
                className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20">
                <ArrowLeft size={18} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">Kartu Tasmi'</p>
                <p className="text-emerald-300 text-[11px]">{santri.name}</p>
              </div>
              <button data-testid="kartu-preview-unduh-pdf-btn"
                onClick={handleUnduhPDF} disabled={downloading}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-60">
                {downloading
                  ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <FileText size={14} />}
                Unduh PDF
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>

              {/* Header kartu */}
              <div className="bg-emerald-950 text-white rounded-2xl px-4 py-4">
                <p className="text-[10px] uppercase tracking-widest text-emerald-400 text-center mb-1">
                  {settings.nama_pesantren || 'Pesantren Tahfizh'}
                </p>
                <p className="text-center font-playfair text-base font-bold">KARTU TASMI' TAHFIZH AL-QUR'AN</p>
              </div>

              {/* Santri info */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    ['Nama Santri', santri.name],
                    ['NIS', santri.nis || '-'],
                    ['Kelas', santri.kelas ? `${santri.kelas} – ${KELAS_MAP[santri.kelas]}` : '-'],
                    ['Halaqah', santri.halaqah_nama || '-'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-gray-400">{label}</p>
                      <p className="font-semibold text-gray-800 mt-0.5">{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabel kartu */}
              {tasmiList.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm">
                  Belum ada data tasmi'
                </div>
              ) : (
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[580px] text-xs">
                      <thead>
                        <tr className="bg-emerald-900 text-white">
                          {['No','Juz','Durasi','Tanggal','Tempat Tasmi\'','Penyimak','Jml Jali','Nilai'].map(h => (
                            <th key={h} className="px-3 py-3 text-left font-semibold text-[11px] whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tasmiList.map((t, i) => (
                          <tr key={t.id} className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/40'}>
                            <td className="px-3 py-2.5 text-gray-400">{i + 1}</td>
                            <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{formatJuz(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap text-gray-600">{formatDurasi(t)}</td>
                            <td className="px-3 py-2.5 whitespace-nowrap">{t.tanggal || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600">{t.tempat || '—'}</td>
                            <td className="px-3 py-2.5 text-gray-600">{t.penyimak || '—'}</td>
                            <td className="px-3 py-2.5 text-center">{t.jumlah_jali ?? '—'}</td>
                            <td className="px-3 py-2.5">
                              <span className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${nilaiCls(nilaiTasmi(t))}`}>{nilaiTasmi(t)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
